import React, { useState, useRef, useMemo } from "react";
import { Card, Button, Typography, Table, Space, Form, Input, Select, InputNumber, Row, Col, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Box, Stack } from "@mui/material";
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  useNodesState, 
  useEdgesState,
  Handle,
  Position 
} from 'reactflow';
import 'reactflow/dist/style.css';
import GenericModal from "../../../buttons/genericModal";
import { backendPatchWithoutRefresh } from "../../../../helpers/callBackend";
import LoadingButton from '@mui/lab/LoadingButton';
import PermissionGate from "../../../../helpers/PermissionGate";

function OwnershipStructure({ ownerships, entityId, entityName, entityData, onOwnershipUpdate }) {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ownershipRows, setOwnershipRows] = useState([]);
  
  const editFormRef = useRef(null);

  const handleModalOpen = () => {
    setVisible(true);
    // Initialize ownership structure if exists
    if (ownerships && ownerships.length > 0) {
      setOwnershipRows(ownerships.map((owner, index) => ({
        id: Date.now() + index,
        entityId: owner.parent_company,
        percentage: parseFloat(owner.ownership_percentage) || 0
      })));
    } else {
      setOwnershipRows([]);
    }
  };

  const handleModalClose = () => {
    if (editFormRef.current) {
      editFormRef.current.resetFields();
    }
    setVisible(false);
    setOwnershipRows([]);
  };

  const handleEdit = (values) => {
    setSubmitting(true);
    
    // Build simplified ownership structure
    const ownershipStructure = ownershipRows.map(row => {
      return {
        child_entity_id: entityId,
        parent_entity_id: row.entityId,
        ownership_percentage: row.percentage
      };
    }).filter(owner => owner.parent_entity_id); // Only include rows with selected entities

    const data = {
      entity_id: entityId,
      name: values.entity_name || entityName,
      ownership_structure: ownershipStructure,
      rename_entity: true
    };

    backendPatchWithoutRefresh("/api/entity/", data)
      .then((res) => {
        // If response contains updated entity data, update the entity
        if (res.data && res.data.entity && onOwnershipUpdate) {
          onOwnershipUpdate(res.data.entity);
        }
        
        setVisible(false);
        setSubmitting(false);
        setOwnershipRows([]);
        message.success('Ownership structure updated successfully');
      }).catch((err) => {
        console.error(err);
        setSubmitting(false);
        message.error('Failed to update ownership structure');
      })
  };

  const addOwnershipRow = () => {
    const currentTotal = ownershipRows.reduce((sum, row) => sum + (row.percentage || 0), 0);
    const remainingPercentage = Math.max(100 - currentTotal, 0);
    
    const newRow = {
      id: Date.now(),
      entityId: null,
      percentage: remainingPercentage
    };
    setOwnershipRows([...ownershipRows, newRow]);
  };

  const removeOwnershipRow = (rowId) => {
    setOwnershipRows(ownershipRows.filter(row => row.id !== rowId));
  };

  const updateOwnershipRow = (rowId, field, value) => {
    setOwnershipRows(ownershipRows.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const getTotalOwnership = () => {
    return ownershipRows.reduce((sum, row) => sum + (row.percentage || 0), 0);
  };

  const isValidOwnership = () => {
    const total = getTotalOwnership();
    return total <= 100;
  };

  const getFilteredEntities = () => {
    return (entityData || []).filter(entity => 
      entity.id !== parseInt(entityId)
    );
  };

  // Custom node component for ancestor entities (parents - on the left)
  const AncestorNode = ({ data }) => {
    return (
      <div style={{
        backgroundColor: '#f5f5f5',
        border: '2px solid #1976d2',
        borderRadius: '8px',
        padding: '12px 16px',
        minWidth: '140px',
        textAlign: 'center',
        fontSize: '13px',
        fontWeight: '500',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '4px', fontWeight: '600' }}>{data.label}</div>
        <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>
          Level {data.level} Parent
        </div>
        {data.ownsCurrentEntity && (
          <div style={{ fontSize: '12px', color: '#1976d2', fontWeight: '600' }}>
            {data.ownsCurrentEntity}%
          </div>
        )}
        <Handle type="source" position={Position.Right} style={{ background: '#1976d2' }} />
        <Handle type="target" position={Position.Left} style={{ background: '#1976d2' }} />
      </div>
    );
  };

  // Custom node component for descendant entities (children - on the right)
  const DescendantNode = ({ data }) => {
    return (
      <div style={{
        backgroundColor: '#f5f5f5',
        border: '2px solid #1976d2',
        borderRadius: '8px',
        padding: '12px 16px',
        minWidth: '140px',
        textAlign: 'center',
        fontSize: '13px',
        fontWeight: '500',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '4px', fontWeight: '600' }}>{data.label}</div>
        <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>
          Level {data.level} Child
        </div>
        {data.ownedByCurrentEntity && (
          <div style={{ fontSize: '12px', color: '#1976d2', fontWeight: '600' }}>
            {data.ownedByCurrentEntity}%
          </div>
        )}
        <Handle type="target" position={Position.Left} style={{ background: '#1976d2' }} />
        <Handle type="source" position={Position.Right} style={{ background: '#1976d2' }} />
      </div>
    );
  };

  // Custom node component for current entity (center)
  const CurrentEntityNode = ({ data }) => {
    return (
      <div style={{
        backgroundColor: '#e3f2fd',
        border: '3px solid #1976d2',
        borderRadius: '12px',
        padding: '20px 24px',
        minWidth: '200px',
        textAlign: 'center',
        fontSize: '16px',
        fontWeight: '700',
        boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
      }}>
        <div style={{ marginBottom: '6px' }}>{data.label}</div>
        <div style={{ fontSize: '12px', color: '#1976d2', fontWeight: '600' }}>
          Current Entity
        </div>
        <Handle type="target" position={Position.Left} style={{ background: '#1976d2' }} />
        <Handle type="source" position={Position.Right} style={{ background: '#1976d2' }} />
        <Handle type="target" position={Position.Top} style={{ background: '#1976d2' }} />
        <Handle type="source" position={Position.Bottom} style={{ background: '#1976d2' }} />
      </div>
    );
  };

  const nodeTypes = useMemo(() => ({
    ancestor: AncestorNode,
    descendant: DescendantNode,
    currentEntity: CurrentEntityNode,
  }), []);

  // Generate nodes and edges for the flowchart
  const { nodes, edges } = useMemo(() => {
    // Handle new data structure
    const ownershipTree = ownerships?.detailed_ownership_tree;
    const relationships = ownershipTree?.all_relationships || [];
    const currentEntity = ownershipTree?.current_entity;
    
    if (!ownershipTree || relationships.length === 0) {
      return { nodes: [], edges: [] };
    }

    const flowNodes = [];
    const flowEdges = [];
    const entityMap = new Map();
    const currentEntityId = parseInt(entityId);

    // Build entity map from all relationships
    relationships.forEach(rel => {
      if (!entityMap.has(rel.parent_company)) {
        entityMap.set(rel.parent_company, {
          id: rel.parent_company,
          name: rel.parent_company_name,
          relationships: []
        });
      }
      if (!entityMap.has(rel.child_company)) {
        entityMap.set(rel.child_company, {
          id: rel.child_company,
          name: rel.child_company_name,
          relationships: []
        });
      }
    });

    // Add current entity if not in map
    if (currentEntity && !entityMap.has(currentEntity.id)) {
      entityMap.set(currentEntity.id, {
        id: currentEntity.id,
        name: currentEntity.name,
        relationships: []
      });
    }

    // Create all entities from relationships
    const allEntities = new Set();
    relationships.forEach(rel => {
      allEntities.add(rel.parent_company);
      allEntities.add(rel.child_company);
    });

    // Calculate levels using BFS from current entity first
    const calculateLevels = () => {
      const visited = new Set();
      const queue = [{ entityId: currentEntityId, level: 0 }];
      const levels = new Map();
      
      levels.set(currentEntityId, 0);
      
      while (queue.length > 0) {
        const { entityId: currentId, level } = queue.shift();
        
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        
        // Find all parents (entities that own this entity)
        relationships.forEach(rel => {
          if (rel.child_company === currentId && !visited.has(rel.parent_company)) {
            const parentLevel = level - 1; // Parents have negative levels
            if (!levels.has(rel.parent_company) || levels.get(rel.parent_company) > parentLevel) {
              levels.set(rel.parent_company, parentLevel);
              queue.push({ entityId: rel.parent_company, level: parentLevel });
            }
          }
        });
        
        // Find all children (entities owned by this entity)
        relationships.forEach(rel => {
          if (rel.parent_company === currentId && !visited.has(rel.child_company)) {
            const childLevel = level + 1; // Children have positive levels
            if (!levels.has(rel.child_company) || levels.get(rel.child_company) < childLevel) {
              levels.set(rel.child_company, childLevel);
              queue.push({ entityId: rel.child_company, level: childLevel });
            }
          }
        });
      }
      
      return levels;
    };

    const entityLevels = calculateLevels();

    // Group entities by level for positioning
    const entitiesByLevel = new Map();
    allEntities.forEach(entityId => {
      const level = entityLevels.get(entityId) || 0;
      if (!entitiesByLevel.has(level)) {
        entitiesByLevel.set(level, []);
      }
      entitiesByLevel.get(level).push(entityId);
    });

    // Position entities horizontally based on their levels
    const entityPositions = new Map();
    const levelSpacing = 300; // Horizontal distance between levels
    const nodeSpacing = 120;  // Vertical spacing between nodes in same level
    const centerX = 0;
    const centerY = 200;

    entitiesByLevel.forEach((entities, level) => {
      const xPosition = centerX + (level * levelSpacing);
      
      entities.forEach((entityId, index) => {
        const yPosition = centerY + (index - (entities.length - 1) / 2) * nodeSpacing;
        entityPositions.set(entityId, { x: xPosition, y: yPosition });
      });
    });

    // Create basic nodes for all entities
    const nodeMap = new Map();
    allEntities.forEach(entityId => {
      const entityInfo = entityMap.get(entityId);
      const position = entityPositions.get(entityId);
      const isCurrentEntity = entityId === currentEntityId;
      
      nodeMap.set(entityId, {
        id: `entity-${entityId}`,
        type: isCurrentEntity ? 'currentEntity' : 'ancestor', // Will be updated later
        position: position,
        data: { 
          label: entityInfo?.name || `Entity ${entityId}`,
          level: 0, // Will be calculated later
          entityId: isCurrentEntity ? currentEntityId : undefined
        },
      });
    });

    // Update nodes with calculated levels and types
    allEntities.forEach(entityId => {
      const node = nodeMap.get(entityId);
      const level = entityLevels.get(entityId) || 0;
      const isCurrentEntity = entityId === currentEntityId;
      
      let nodeType = 'ancestor';
      let relationshipInfo = {};
      
      if (isCurrentEntity) {
        nodeType = 'currentEntity';
      } else if (level < 0) {
        // This is a parent/ancestor
        nodeType = 'ancestor';
        const parentRel = relationships.find(rel => 
          rel.parent_company === entityId && rel.child_company === currentEntityId
        );
        if (parentRel) {
          relationshipInfo.ownsCurrentEntity = parentRel.ownership_percentage;
        }
      } else if (level > 0) {
        // This is a child/descendant
        nodeType = 'descendant';
        const childRel = relationships.find(rel => 
          rel.child_company === entityId && rel.parent_company === currentEntityId
        );
        if (childRel) {
          relationshipInfo.ownedByCurrentEntity = childRel.ownership_percentage;
        }
      }

      node.type = nodeType;
      node.data.level = Math.abs(level);
      Object.assign(node.data, relationshipInfo);
      
      flowNodes.push(node);
    });

    // Create edges for ALL relationships
    relationships.forEach(rel => {
      const sourceId = `entity-${rel.parent_company}`;
      const targetId = `entity-${rel.child_company}`;
      
      flowEdges.push({
        id: `edge-${rel.id}`,
        source: sourceId,
        target: targetId,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#1976d2', strokeWidth: 2 },
        label: `${rel.ownership_percentage}%`,
        labelStyle: { fill: '#1976d2', fontWeight: 600 },
        markerEnd: {
          type: 'arrowclosed',
          color: '#1976d2',
          width: 20,
          height: 20
        }
      });
    });


    return { nodes: flowNodes, edges: flowEdges };
  }, [ownerships, entityName, entityId]);

  const [flowNodes, setNodes, onNodesChange] = useNodesState(nodes);
  const [flowEdges, setEdges, onEdgesChange] = useEdgesState(edges);

  // Update nodes when ownerships change
  React.useEffect(() => {
    setNodes(nodes);
    setEdges(edges);
  }, [nodes, edges, setNodes, setEdges]);

  return (
    <Card 
      style={{ marginBottom: '24px' }}
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Ownership Structure
        </Typography.Title>
      }
      extra={
        <PermissionGate
          requiredRole='dashboardEditor'
          toolTipText='Only admins and dashboard editors may edit ownership structure'
          readOnly={
            <Button disabled size="small">
              Edit Ownership
            </Button>
          }
        >
          <GenericModal
            visible={visible}
            handleCancelModal={handleModalClose}
            showModal={handleModalOpen}
            size="md"
            button={<Button size="small">Edit Ownership</Button>}
            modalTitle={`Edit Ownership Structure - ${entityName}`}
            modalBody={
              <Form
                layout="vertical"
                name="ownership_edit"
                onFinish={handleEdit}
                autoComplete="off"
                initialValues={{
                  entity_name: entityName,
                }}
                ref={editFormRef}
              >
                {/* Entity Name Section */}
                <Form.Item
                  name="entity_name"
                  label="Entity Name"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                {/* Ownership Structure Section */}
                <div style={{ marginTop: '24px' }}>
                  <Typography.Title level={5} style={{ marginBottom: '8px' }}>
                    Ownership Structure
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '16px' }}>
                    If the entity is owned by parent entities, log them here with the ownership percentage of this entity.
                  </Typography.Text>

                  {/* Existing Owner Rows */}
                  {ownershipRows.map((row, index) => (
                    <Row key={row.id} gutter={[12, 8]} style={{ marginBottom: '12px', alignItems: 'center', width: '100%' }}>
                      <Col span={16} style={{ paddingRight: '8px' }}>
                        <Select
                          placeholder="Entity dropdown"
                          value={row.entityId}
                          onChange={(value) => updateOwnershipRow(row.id, 'entityId', value)}
                          style={{ width: '100%' }}
                          showSearch
                          optionFilterProp="children"
                        >
                          {getFilteredEntities().map(entity => (
                            <Select.Option key={entity.id} value={entity.id}>
                              {entity.entity_info?.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={6} style={{ paddingRight: '8px' }}>
                        <InputNumber
                          value={row.percentage}
                          onChange={(value) => updateOwnershipRow(row.id, 'percentage', value)}
                          min={0}
                          max={100}
                          formatter={value => `${value}%`}
                          parser={value => value.replace('%', '')}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => removeOwnershipRow(row.id)}
                          size="small"
                          style={{ color: '#ff4d4f', padding: '4px' }}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* Add Owner Button */}
                  <div
                    style={{
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px',
                      padding: '16px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      marginBottom: '16px',
                      ':hover': { borderColor: '#40a9ff' }
                    }}
                    onClick={addOwnershipRow}
                  >
                    <Button type="dashed" icon={<PlusOutlined />} style={{ border: 'none' }}>
                      Add owner
                    </Button>
                  </div>

                  {/* Validation Error */}
                  {!isValidOwnership() && (
                    <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '16px' }}>
                      Total ownership percentage cannot exceed 100%. Current total: {getTotalOwnership()}%
                    </div>
                  )}
                </div>

                <Form.Item>
                  <LoadingButton
                    htmlType="submit"
                    style={{ margin: "3px", width: "100%" }}
                    loading={submitting}
                    loadingPosition="start"
                    disableElevation
                    fullWidth
                    size="small"
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                    disabled={!isValidOwnership()}
                  >
                    Submit
                  </LoadingButton>
                </Form.Item>
              </Form>
            }
          />
        </PermissionGate>
      }
    >
      {ownerships?.detailed_ownership_tree?.all_relationships?.length > 0 ? (
        <div>
          <Typography.Paragraph style={{ marginBottom: '16px', color: '#666' }}>
            Parent percentage represents the parent's percent ownership of this entity. Child percentage represents percent ownership this entity has of the child.
          </Typography.Paragraph>
          
          {/* React Flow Ownership Chart */}
          <div style={{ width: '100%', height: '400px' }}>
            <ReactFlow
              nodes={flowNodes}
              edges={flowEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ 
                padding: 0.2,
                includeHiddenNodes: false 
              }}
              minZoom={0.3}
              maxZoom={2}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              elementsSelectable={true}
              nodesDraggable={true}
              panOnDrag={true}
              zoomOnScroll={true}
              zoomOnPinch={true}
              zoomOnDoubleClick={true}
              panOnScrollMode="free"
              selectionOnDrag={false}
            >
              <Background 
                variant="dots" 
                gap={12} 
                size={1} 
                color="#f0f0f0" 
              />
              <Controls 
                showZoom={true}
                showFitView={true}
                showInteractive={true}
              />
              <MiniMap 
                nodeColor="#f0f0f0"
                nodeStrokeColor="#1976d2"
                nodeStrokeWidth={2}
                maskColor="#f8f9fa"
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e0e0e0'
                }}
              />
            </ReactFlow>
          </div>

          {/* Ownership Summary */}
          <Box sx={{
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 1,
            px: 2,
            py: 1,
            textAlign: 'center',
            mt: 2
          }}>
            <Typography variant="caption" color="text.secondary">
              Total Entities in Network: {ownerships.detailed_ownership_tree.total_entities} | 
              Relationships: {ownerships.detailed_ownership_tree.all_relationships.length} | 
              Inbound Ownership: {ownerships.detailed_ownership_tree.all_relationships
                .filter(rel => rel.child_company === parseInt(entityId) && rel.relationship_type === 'ancestor')
                .reduce((sum, rel) => sum + parseFloat(rel.ownership_percentage), 0).toFixed(2)}%
            </Typography>
          </Box>
        </div>
      ) : (
        <Typography.Text type="secondary">
          This entity is not owned by a parent entity and does not have ownership of a child entity.
        </Typography.Text>
      )}
    </Card>
  );
}

export default OwnershipStructure;
